import React from 'react';
import RGL from 'react-grid-layout';
import { Controller, useForm } from 'react-hook-form';

import {
  getNewWidgetLayout,
  upsertWidget,
} from 'dashboards/services/dashboard';
import { useDashboard } from 'dashboards/hooks/useDashboard';
import { useGateways } from 'gateways/hooks/useGateways';
import { useModal } from 'hooks/useModal';
import { useCameras, UseCamerasParams } from 'cameras/hooks/useCameras';
import { useUpdateDashboard } from 'dashboards/hooks/useUpdateDashboard';

import { CamerasWidget } from 'types/dashboard_widget';

import { Button } from 'components/Button/Button';
import { Field } from 'components/Field/Field';
import { GatewaySelect } from 'gateways/components/GatewaySelect/GatewaySelect';
import { Icon } from 'components/Icon/IconDeprecated';
import { IconButton } from 'components/IconButton/IconButtonDeprecated';
import { Input } from 'components/Input/Input';
import { LightSwitch } from 'components/LightSwitch/LightSwitch';
import { Loader } from 'components/Loader/Loader';
import { Select } from 'components/Select/Select';
import { CameraSelect } from 'cameras/components/CameraSelect/CameraSelect';

import { WidgetFormHeader } from './Header';
import { WidgetFormFooter } from './Footer';

const FILTER_OPTIONS = [
  { label: 'Gateways', value: 'gateway_ids' },
  { label: 'Cameras', value: 'camera_ids' },
] as const;

type CamerasWidgetFieldValues = {
  name: string;
  gateway_ids: Array<string>;
  camera_ids: Array<string>;
  autoPlay: boolean;
};

type CamerasFormProps = { widget?: CamerasWidget; dashboardId: string };

export function CamerasForm({ widget, dashboardId }: CamerasFormProps) {
  const { close } = useModal();

  const { data: dashboard } = useDashboard(dashboardId);

  const options = widget ? widget.options : {};

  const {
    gateway_ids = [],
    camera_ids = [],
  } = options;

  const { data: initialGateways, isInitialLoading: isGatewaysLoading } =
    useGateways({
      params: {
        gateway_ids,
        limit: gateway_ids.length,
      },
      enabled: gateway_ids.length > 0,
    });

  const { data: initialCameras, isInitialLoading: isCamerasLoading } =
    useCameras({
      params: {
        camera_ids,
        limit: camera_ids.length,
      },
      enabled: camera_ids.length > 0,
    });

  const isLoading = isGatewaysLoading || isCamerasLoading;

  const { mutate, isLoading: isUpdating } = useUpdateDashboard({
    onSuccess: () => close(),
  });

  const {
    control,
    formState: { errors, dirtyFields },
    handleSubmit,
    setValue,
    register,
  } = useForm<CamerasWidgetFieldValues>({
    defaultValues: {
      name: widget?.name,
      autoPlay: widget?.autoPlay ?? true,
      gateway_ids,
      camera_ids,
    },
  });

  const isDirty = Boolean(Object.keys(dirtyFields).length);

  const [filters, setFilters] = React.useState<
    Array<{
      entityType: 'gateway_ids' | 'camera_ids' | 'undecided';
    }>
  >(() => {
    const initialFilters = [];

    if (gateway_ids.length > 0) {
      initialFilters.push({
        entityType: 'gateway_ids' as const,
      });
    }

    if (camera_ids.length > 0) {
      initialFilters.push({
        entityType: 'camera_ids' as const,
      });
    }

    return initialFilters;
  });

  function onSubmit({
    name,
    gateway_ids,
    camera_ids,
    autoPlay,
  }: CamerasWidgetFieldValues) {
    if (!dashboard) {
      return;
    }

    const id = widget?.id || crypto.randomUUID();
    const layout: Omit<RGL.Layout, 'i'> =
      widget?.layout ||
      getNewWidgetLayout(dashboard.configuration.widgets, 6, 6);

    const options: UseCamerasParams = {
      gateway_ids: gateway_ids.length > 0 ? gateway_ids : undefined,
      camera_ids: camera_ids.length > 0 ? camera_ids : undefined,
    };

    const updatedDashboard = upsertWidget(
      dashboard,
      new CamerasWidget(id, name, options, autoPlay, layout)
    );

    mutate({
      id: updatedDashboard.id,
      configuration: updatedDashboard.configuration,
    });
  }

  function getAvailableOptions() {
    return FILTER_OPTIONS.filter(
      (option) =>
        !new Set(
          filters
            .filter((filter) => filter.entityType !== 'undecided')
            .map((filter) => filter.entityType)
        ).has(option.value)
    );
  }

  const canAdd = filters.length < FILTER_OPTIONS.length;

  return (
    <form className="flex h-full flex-col" onSubmit={handleSubmit(onSubmit)}>
      <WidgetFormHeader type="cameras" />
      <Field label="Widget name" error={errors.name}>
        <Input
          {...register('name', {
            required: 'Please choose a name for this widget.',
          })}
          type="text"
          autoComplete="off"
          spellCheck="false"
        />
      </Field>
      <Field label="Enable autoplay">
        <LightSwitch {...register('autoPlay')} />
      </Field>

      <Field
        label="Filters"
        error={errors.gateway_ids || errors.camera_ids}
      >
        {isLoading ? (
          <Loader size="small" text="Loading filters" />
        ) : (
          <>
            <div className="flex flex-col gap-2">
              {filters.map((filter, index) => (
                <div
                  className="grid grid-cols-[1fr_3fr_auto] items-center gap-2"
                  key={`${filter.entityType}-${index}`}
                >
                  <div>
                    <Select
                      aria-label="Select type"
                      options={getAvailableOptions()}
                      value={FILTER_OPTIONS.find(
                        ({ value }) => value === filter.entityType
                      )}
                      onChange={(option) => {
                        if (!option) return;

                        setFilters((previousFilters) =>
                          previousFilters.map((filter, i) => {
                            if (i === index) {
                              if (filter.entityType !== 'undecided') {
                                setValue(filter.entityType, []);
                              }
                              return { entityType: option.value };
                            }
                            return filter;
                          })
                        );
                      }}
                    />
                  </div>
                  <div>
                    {filter.entityType !== 'undecided' ? (
                      <Controller
                        control={control}
                        name={filter.entityType}
                        render={({ field: { onChange } }) => {
                          switch (filter.entityType) {
                            case 'gateway_ids':
                              return (
                                <GatewaySelect
                                  defaultValue={initialGateways?.data}
                                  onChange={(options) =>
                                    options &&
                                    onChange(
                                      options.map((gateway) => gateway.id)
                                    )
                                  }
                                  isMulti
                                />
                              );
                            case 'camera_ids':
                              return (
                                <CameraSelect
                                  defaultValue={initialCameras?.data}
                                  onChange={(options) =>
                                    options &&
                                    onChange(
                                      options.map((camera) => camera.id)
                                    )
                                  }
                                  isMulti
                                />
                              );
                            default:
                              return <Select options={[]} isDisabled />;
                          }
                        }}
                        rules={{ required: true }}
                      />
                    ) : (
                      <Select options={[]} isDisabled />
                    )}
                  </div>
                  <IconButton
                    icon="cancel"
                    label="Remove filter"
                    onClick={() => {
                      setFilters((previousFilters) => {
                        const filterToRemove = previousFilters[index];
                        if (filterToRemove.entityType !== 'undecided') {
                          setValue(filterToRemove.entityType, []);
                        }
                        return previousFilters.filter((_, i) => i !== index);
                      });
                    }}
                  />
                </div>
              ))}
            </div>
            <div>
              <Button
                variant="secondary"
                size="small"
                onClick={() => {
                  setFilters((previousFilters) => [
                    ...previousFilters,
                    { entityType: 'undecided' },
                  ]);
                }}
                disabled={
                  !canAdd ||
                  isLoading ||
                  filters.at(-1)?.entityType === 'undecided'
                }
              >
                <Icon name="plus" size="xsmall" />
                <span>Add filter</span>
              </Button>
            </div>
          </>
        )}
      </Field>

      <WidgetFormFooter isDirty={isDirty} isLoading={isUpdating || isLoading} />
    </form>
  );
}

